import * as React from "react";

import { TagInput } from "@/modern/components/tag-input";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import type { TagsMetaItemConfig } from "../constants/editorMetaItems";
import { TAGS_KEYS } from "@/other/constants/TagsKeys";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { useMemo } from "react";

interface EditorTagFieldProps {
  tagConfig: TagsMetaItemConfig;
  value: number[];
  onValueChange: (values: number[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

export const EditorTagField = ({
  tagConfig,
  value = [],
  onValueChange,
  placeholder = "Add tag...",
  disabled = false,
}: EditorTagFieldProps) => {
  const { project, user } = useSwivuBryntumContext();
  const { version } = useSwivuStore({ storeId: tagConfig.storeKey });

  const canCreateTags = useMemo<boolean>(() => {
    if (!user) return false;
    return user.can(GRANTS.grant_one);
  }, [user])

  const items = React.useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const store = (project as any).getCrudStore(tagConfig.storeKey);
    if (!store) return [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (store.query((r: any) => tagConfig.filterFn(r) && !r.isPhantom) ?? []).map((r: any) => ({
      value: String(r[tagConfig.fieldConfig.valueField]),
      label: r[tagConfig.fieldConfig.displayField] as string,
    }));
  }, [version, project, tagConfig]);

  const stringValue = React.useMemo(() => value.map(String), [value]);
  const [isCreating, setIsCreating] = React.useState(false);

  const handleValueChange = React.useCallback(
    (values: string[]) => {
      onValueChange(values.map(Number));
    },
    [onValueChange],
  );

  const handleCreateOption = React.useCallback(
    async (label: string) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const store = (project as any).getCrudStore(tagConfig.storeKey);
      if (!store) return;

      setIsCreating(true);
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const [newRecord] = store.add({
          [tagConfig.fieldConfig.displayField]: label,
          tagSet: TAGS_KEYS.tag,
        });

        await project.sync();

        // Select the newly created tag immediately.
        handleValueChange([...stringValue, String(newRecord[tagConfig.fieldConfig.valueField])]);
      } finally {
        setIsCreating(false);
      }
    },
    [project, tagConfig, stringValue, handleValueChange],
  );

  return (
    <TagInput
      items={items}
      value={stringValue}
      onValueChange={handleValueChange}
      onCreateOption={canCreateTags ? handleCreateOption : undefined}
      isCreating={isCreating}
      placeholder={placeholder}
      disabled={disabled || isCreating}
    />
  );
};
